import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { isGlobalScope, IssueFilterKeys, SeveritySource } from '../../../../../shared/issuesV2';
import { VulnSeverityMapping, vulnSeverityMappingToString } from '../../../../../shared/vulns/severity';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { useSetFilterHasChanged } from '../../../../redux/savedFilters/hooks';
import FilterCardCheckboxes from '../../../generic/Filter/FilterCardCheckboxes';
import FilterPanel from '../../../generic/Filter/FilterPanel';
import useFetchIssuesAction from '../../IssuesInbox/hooks/useFetchIssuesAction';
import type { SelectOption, SelectOptionWithAttributes } from '../../../../types';
import type { SelectedOptionsProps } from '../../../generic/Filter/CheckboxFilterGroup';

const SEVERITY_OPTIONS: SelectOptionWithAttributes[] = [
  { value: vulnSeverityMappingToString(VulnSeverityMapping.CRITICAL), label: 'Critical' },
  { value: vulnSeverityMappingToString(VulnSeverityMapping.HIGH), label: 'High' },
  { value: vulnSeverityMappingToString(VulnSeverityMapping.MEDIUM), label: 'Medium' },
  { value: vulnSeverityMappingToString(VulnSeverityMapping.LOW), label: 'Low' },
  { value: vulnSeverityMappingToString(VulnSeverityMapping.UNKNOWN), label: 'Unknown' },
];

const SEVERITY_OPTIONS_WITH_RANGE: SelectOptionWithAttributes[] = [
  { value: vulnSeverityMappingToString(VulnSeverityMapping.CRITICAL), label: 'Critical (9-10)' },
  { value: vulnSeverityMappingToString(VulnSeverityMapping.HIGH), label: 'High (7-8.9)' },
  { value: vulnSeverityMappingToString(VulnSeverityMapping.MEDIUM), label: 'Medium (4-6.9)' },
  { value: vulnSeverityMappingToString(VulnSeverityMapping.LOW), label: 'Low (0.1-3.9)' },
  { value: vulnSeverityMappingToString(VulnSeverityMapping.UNKNOWN), label: 'Unknown' },
];

const SEVERITY_SOURCE_OPTIONS: SelectOptionWithAttributes[] = [
  { value: SeveritySource.Standard, label: 'Standard (CVSS)', tooltip: 'Critical: 9-10\nHigh: 7-8.9\nMedium: 4-6.9\nLow: 0.1-3.9' },
  { value: SeveritySource.Custom, label: 'Custom Risk Score', tooltip: 'Critical: 90-100\nHigh: 70-89\nMedium: 40-69\nLow: 1-39' },
];

type Props = Omit<SelectedOptionsProps, 'onSelect'> & {
  selectedSeveritySourceOptions?: string[];
};

const SeverityFilter: VoidFunctionComponent<Props> = ({ selectedOptions, selectedSeveritySourceOptions }) => {
  const setFilters = useFetchIssuesAction(IssuesV2Store.actions.setFilter);
  const stateFilters = useSelector(IssuesV2Store.selectors.getFilter);
  const issueScope = useSelector(IssuesV2Store.selectors.getScope);
  const setFilterHasChanged = useSetFilterHasChanged();
  const shouldShowSeveritySourceFilter = !isGlobalScope(issueScope);
  const severityOptions = shouldShowSeveritySourceFilter ? SEVERITY_OPTIONS : SEVERITY_OPTIONS_WITH_RANGE;

  const selectedCheckboxes = (selectedOptions ?? []).map((option: string) => ({
    label: severityOptions.find(filterOption => filterOption.value === option)?.label || option,
    value: option,
  }));

  const onSeverityChange = (newSelected: SelectOption[]) => {
    setFilters({
      ...stateFilters,
      [IssueFilterKeys.Severity]: newSelected.map(({ value }) => value),
    });
    setFilterHasChanged(true);
  };

  const selectedSourceCheckboxes = (selectedSeveritySourceOptions ?? []).map((option: string) => ({
    label: SEVERITY_SOURCE_OPTIONS.find(filterOption => filterOption.value === option)?.label || option,
    value: option,
  }));

  const onSeveritySourceChange = (newSelected: SelectOption[]) => {
    setFilters({
      ...stateFilters,
      [IssueFilterKeys.SeveritySource]: newSelected.map(({ value }) => value),
    });
    setFilterHasChanged(true);
  };

  return (
    <FilterPanel>
      <FilterCardCheckboxes
        label="Severity"
        options={severityOptions}
        values={selectedCheckboxes}
        onChange={onSeverityChange}
        testId="issues-vuln-severity"
      />
      {shouldShowSeveritySourceFilter && (
        <FilterCardCheckboxes
          label="Severity Source"
          options={SEVERITY_SOURCE_OPTIONS}
          values={selectedSourceCheckboxes}
          onChange={onSeveritySourceChange}
          testId="issues-vuln-severity-source"
        />
      )}
    </FilterPanel>
  );
};

export default SeverityFilter;
